import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useForm, useFieldArray } from 'react-hook-form';
import { Fragment, useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { api } from 'src/auditoria/infra/http/api-client';
import { TxForm } from 'src/auditoria/components/tx-form/tx-form';
import {
  DsNum,
  DsIcon,
  DsField,
  DsButton,
  DsOverline,
  DsPageHeader,
  DsIconButton,
  DsStatusChip,
} from 'src/auditoria/components/ds';

import { Field } from 'src/components/hook-form';

import {
  type Servico,
  type Tomador,
  type Atividade,
  type EmitNfseBody,
  type EmitNfseResponse,
  formatCurrency,
} from './types';

// ----------------------------------------------------------------------
// NFS-e → Emissão (portal SN). WIZARD de 3 passos (Tomador → Serviço/valores →
// Revisar) com resumo fixo de 300px à direita. Regra de negócio idêntica: o
// tomador vai na nota com endereço/contato (whitelist tm_* de rps.gerar_nfse);
// prestador/protocolo/número do RPS são resolvidos NO SERVIDOR. O RHF mantém o
// estado entre os passos.
//
// Os três artboards `portal-nfse-emitir*.dc.html` são ESTADOS desta tela, não
// telas distintas. O que eles prescrevem e aqui virou código:
//  - o stepper vive DENTRO do card do formulário, com dot de 26px (`.stepdot`):
//    feito = primary sólido, atual = anel de 2px, próximo = `background.neutral`;
//  - campos com rótulo externo (`.flabel` + `.fld`), nunca label flutuante;
//  - a navegação (`.btnsec` Voltar / `.btnpri` avançar) é o rodapé do card do
//    formulário — não do resumo;
//  - o passo 3 é uma pilha de `.kv` sob overlines de seção.
// ----------------------------------------------------------------------

interface ItemForm {
  descricao: string;
  quantidade: number;
  valor_unitario: number;
}
interface EmitFormValues {
  tomadorSalvo: Tomador | null;
  tm_cpfcnpj: string;
  tm_razaosocial: string;
  tm_cep: string;
  tm_endereco: string;
  tm_numero: string;
  tm_complemento: string;
  tm_bairro: string;
  tm_uf: string;
  tm_telefone: string;
  tm_email: string;
  atividade: Atividade | null;
  servico: Servico | null;
  aliquota: number;
  discriminacao: string;
  items: ItemForm[];
}

const EMPTY_ITEM: ItemForm = {
  descricao: '',
  quantidade: 1,
  valor_unitario: 0,
};
const DEFAULTS: EmitFormValues = {
  tomadorSalvo: null,
  tm_cpfcnpj: '',
  tm_razaosocial: '',
  tm_cep: '',
  tm_endereco: '',
  tm_numero: '',
  tm_complemento: '',
  tm_bairro: '',
  tm_uf: '',
  tm_telefone: '',
  tm_email: '',
  atividade: null,
  servico: null,
  aliquota: 0,
  discriminacao: '',
  items: [{ ...EMPTY_ITEM }],
};
const PASSOS = ['Tomador', 'Serviço e valores', 'Revisar'] as const;

/** Rodapé do resumo e do passo 3. Série e competência ainda não vêm do backend. */
const SELO_SERIE = 'Série A · competência 04/2025';

function limpo(v: string): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

// ----------------------------------------------------------------------
// `.stepdot` — o stepper do canvas, dentro do card do formulário.
// ----------------------------------------------------------------------

const SX_DOT = {
  width: 26,
  height: 26,
  flex: '0 0 26px',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  fontSize: 12,
  fontWeight: 800,
} satisfies SystemStyleObject<Theme>;

const SX_DOT_FEITO = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
} satisfies SystemStyleObject<Theme>;
const SX_DOT_PROXIMO = {
  bgcolor: 'background.neutral',
  color: 'text.disabled',
} satisfies SystemStyleObject<Theme>;

/** `.stepcur`: anel de 2px em primary; no escuro o texto vira `primary.light`. */
const sxDotAtual = (theme: Theme) => ({
  border: '2px solid',
  borderColor: theme.vars.palette.primary.main,
  color: theme.vars.palette.primary.dark,
  backgroundColor: 'transparent',
  ...theme.applyStyles('dark', { color: theme.vars.palette.primary.light }),
});

function PassosCanvas({ atual }: { atual: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: '16px' }}>
      {PASSOS.map((rotulo, indice) => (
        <Fragment key={rotulo}>
          {indice > 0 && (
            <Box aria-hidden sx={{ flex: 1, height: '2px', bgcolor: 'divider', m: '0 10px' }} />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              sx={[
                SX_DOT,
                indice < atual && SX_DOT_FEITO,
                indice > atual && SX_DOT_PROXIMO,
                indice === atual && sxDotAtual,
              ]}
            >
              {indice < atual ? <DsIcon name="check" size={14} /> : indice + 1}
            </Box>
            <Box component="span" sx={{ fontSize: 12, fontWeight: 800 }}>
              {rotulo}
            </Box>
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

// ----------------------------------------------------------------------
// `.kv` — a linha rótulo/valor do resumo e da revisão.
// ----------------------------------------------------------------------

function LinhaResumo({
  rotulo,
  valor,
  forte,
}: {
  rotulo: string;
  valor: React.ReactNode;
  forte?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '7px 0',
        borderBottom: '1px dashed',
        borderColor: 'divider',
        fontSize: 12,
      }}
    >
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {rotulo}
      </Box>
      <Box component="b" sx={{ textAlign: 'right', minWidth: 0, ...(forte && { fontSize: 15 }) }}>
        <DsNum>{valor || '—'}</DsNum>
      </Box>
    </Box>
  );
}

/** Pilha de `.kv`: a última linha do bloco não desenha a divisa. */
function PilhaResumo({ children }: { children: React.ReactNode }) {
  return <Box sx={{ '& > div:last-of-type': { borderBottom: 0 } }}>{children}</Box>;
}

// ----------------------------------------------------------------------

export default function NfseEmitirPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [enviando, setEnviando] = useState(false);

  const [atividadeQuery, setAtividadeQuery] = useState('');
  const [servicoQuery, setServicoQuery] = useState('');
  const [tomadorQuery, setTomadorQuery] = useState('');

  const { data: atividades, isLoading: loadingAtividades } = useSWR<Atividade[]>(
    `/portal-api/nfse/emitir/atividades?q=${encodeURIComponent(atividadeQuery)}`,
    fetcher
  );
  const { data: servicos, isLoading: loadingServicos } = useSWR<Servico[]>(
    `/portal-api/nfse/emitir/servicos?q=${encodeURIComponent(servicoQuery)}`,
    fetcher
  );
  const { data: tomadores, isLoading: loadingTomadores } = useSWR<Tomador[]>(
    tomadorQuery.trim().length >= 3
      ? `/portal-api/nfse/emitir/tomadores?q=${encodeURIComponent(tomadorQuery)}`
      : null,
    fetcher
  );

  const methods = useForm<EmitFormValues>({ defaultValues: DEFAULTS });
  const { control, watch, setValue } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const items = watch('items') ?? [];
  const atividade = watch('atividade');
  const servico = watch('servico');
  const tomadorSalvo = watch('tomadorSalvo');
  const cpfcnpj = watch('tm_cpfcnpj') ?? '';
  const razaosocial = watch('tm_razaosocial') ?? '';
  const discriminacao = watch('discriminacao') ?? '';
  const aliquota = watch('aliquota') ?? 0;

  useEffect(() => {
    if (tomadorSalvo) {
      setValue('tm_cpfcnpj', tomadorSalvo.cpf_cnpj ?? '');
      setValue('tm_razaosocial', tomadorSalvo.nome ?? '');
    }
  }, [tomadorSalvo, setValue]);

  useEffect(() => {
    if (servico?.aliquota !== undefined && servico?.aliquota !== null) {
      setValue('aliquota', servico.aliquota);
    }
  }, [servico, setValue]);

  const valorTotal = useMemo(
    () =>
      items.reduce((s, it) => s + Number(it.quantidade || 0) * Number(it.valor_unitario || 0), 0),
    [items]
  );
  const issValor = Number(aliquota) ? Math.round(valorTotal * Number(aliquota)) / 100 : 0;

  const step0Ok = !!String(cpfcnpj).trim();
  const step1Ok = !!String(discriminacao).trim() && valorTotal > 0;
  const canEmit = step0Ok && step1Ok;

  const rotuloIss = aliquota ? `ISS ${aliquota}%` : 'ISS';

  const avancar = () => {
    if (step === 0 && !step0Ok) return toast.error('Informe o CPF/CNPJ do tomador.');
    if (step === 1 && !step1Ok)
      return toast.error('Informe a descrição e ao menos um item com valor.');
    return setStep((s) => Math.min(s + 1, 2));
  };

  const voltar = () => {
    if (step === 0) {
      navigate('/nfse');
      return;
    }
    setStep((s) => s - 1);
  };

  const onSubmit = async (data: EmitFormValues) => {
    if (!canEmit) return;
    setEnviando(true);
    try {
      const itensDetalhe = data.items
        .filter((it) => String(it.descricao).trim())
        .map(
          (it) =>
            `${Number(it.quantidade)} x ${String(it.descricao).trim()} — ${formatCurrency(Number(it.quantidade) * Number(it.valor_unitario))}`
        )
        .join('\n');
      const disc = String(data.discriminacao).trim();
      const discriminacaoFinal = itensDetalhe ? `${disc}\n\nItens:\n${itensDetalhe}` : disc;
      const aliq = Number(data.aliquota) || 0;

      const body: EmitNfseBody = {
        tm_cpfcnpj: data.tm_cpfcnpj.replace(/\D/g, ''),
        tm_razaosocial: limpo(data.tm_razaosocial),
        tm_cep: limpo(data.tm_cep),
        tm_endereco: limpo(data.tm_endereco),
        tm_numero: limpo(data.tm_numero),
        tm_complemento: limpo(data.tm_complemento),
        tm_bairro: limpo(data.tm_bairro),
        tm_uf: limpo(data.tm_uf)?.toUpperCase(),
        tm_telefone: limpo(data.tm_telefone),
        tm_email: limpo(data.tm_email),
        sv_discriminacao: discriminacaoFinal,
        sv_cnae: data.atividade?.codigo || undefined,
        sv_itemlistaservico: data.servico?.codigo || undefined,
        vl_servico: valorTotal,
        vl_aliquota: aliq || undefined,
        vl_iss: aliq ? Math.round(valorTotal * aliq) / 100 : undefined,
      };
      const res = await api.post<EmitNfseResponse>('/portal-api/nfse/emitir', body);
      toast.success(`NFS-e ${res.numero || ''} emitida com sucesso.`);
      navigate(`/nfse/${res.nfse_id}`);
    } catch (erro) {
      const mensagem = (erro as { response?: { data?: { error?: string } } })?.response?.data
        ?.error;
      toast.error(mensagem ?? 'Falha ao emitir a nota.');
    } finally {
      setEnviando(false);
    }
  };

  const resumo = (
    <Card
      sx={{
        flex: { md: '0 0 300px' },
        width: { xs: '100%', md: 300 },
        padding: '18px 20px',
        position: { md: 'sticky' },
        top: { md: 88 },
      }}
    >
      <Box component="p" sx={{ fontSize: 13, fontWeight: 800, m: '0 0 6px' }}>
        Resumo da nota
      </Box>
      <PilhaResumo>
        <LinhaResumo rotulo="Tomador" valor={razaosocial} />
        <LinhaResumo rotulo="CPF/CNPJ" valor={cpfcnpj} />
        <LinhaResumo rotulo="Valor dos serviços" valor={formatCurrency(valorTotal)} />
        <LinhaResumo rotulo={rotuloIss} valor={formatCurrency(issValor)} />
        <LinhaResumo rotulo="INSS / COFINS / CSLL" valor={formatCurrency(0)} />
        <LinhaResumo rotulo="Valor da nota" valor={formatCurrency(valorTotal)} forte />
      </PilhaResumo>
      <Box sx={{ fontSize: 11, color: 'text.secondary', mt: '10px' }}>
        Autenticação gerada após a emissão · {SELO_SERIE}
      </Box>
    </Card>
  );

  return (
    <DashboardContent>
      <DsPageHeader
        title="Emitir NFS-e"
        weight={800}
        subtitle="Wizard em 3 passos com resumo fixo · tomador, serviço e valores, revisão."
        action={<DsStatusChip tone="info">{`Passo ${step + 1} de 3`}</DsStatusChip>}
      />

      <TxForm<EmitFormValues> methods={methods} onSubmit={onSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            gap: '16px',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
            <Card sx={{ padding: '18px 20px' }}>
              <PassosCanvas atual={step} />

              {step === 0 && (
                <>
                  <Box sx={{ fontSize: 14, fontWeight: 800, mb: '10px' }}>Tomador do serviço</Box>

                  <Box sx={{ mb: '10px' }}>
                    <DsField label="Buscar tomador salvo" density="sm">
                      <Field.Autocomplete
                        name="tomadorSalvo"
                        placeholder="Nome ou CPF/CNPJ (mín. 3 caracteres)…"
                        options={tomadores ?? []}
                        loading={loadingTomadores}
                        filterOptions={(x) => x}
                        onInputChange={(_e, value, reason) => {
                          if (reason === 'input') setTomadorQuery(value);
                        }}
                        getOptionLabel={(opt) =>
                          typeof opt === 'string' ? opt : `${opt.nome} (${opt.cpf_cnpj})`
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        noOptionsText="Digite ao menos 3 caracteres (ou preencha abaixo)"
                      />
                    </DsField>
                  </Box>

                  <DsOverline>ou informe os dados</DsOverline>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                      gap: '10px',
                      mb: '10px',
                    }}
                  >
                    <DsField label="CPF / CNPJ *" density="sm">
                      <Field.Text name="tm_cpfcnpj" />
                    </DsField>
                    <DsField label="Nome / razão social" density="sm">
                      <Field.Text name="tm_razaosocial" />
                    </DsField>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 2fr 1fr' },
                      gap: '10px',
                      mb: '10px',
                    }}
                  >
                    <DsField label="CEP" density="sm">
                      <Field.Text name="tm_cep" />
                    </DsField>
                    <DsField label="Endereço" density="sm">
                      <Field.Text name="tm_endereco" />
                    </DsField>
                    <DsField label="Número" density="sm">
                      <Field.Text name="tm_numero" />
                    </DsField>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 1fr' },
                      gap: '10px',
                      mb: '10px',
                    }}
                  >
                    <DsField label="Bairro" density="sm">
                      <Field.Text name="tm_bairro" />
                    </DsField>
                    <DsField label="Complemento" density="sm">
                      <Field.Text name="tm_complemento" />
                    </DsField>
                    <DsField label="UF" density="sm">
                      <Field.Text name="tm_uf" />
                    </DsField>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: '10px',
                    }}
                  >
                    <DsField label="Telefone" density="sm">
                      <Field.Text name="tm_telefone" />
                    </DsField>
                    <DsField label="E-mail" density="sm">
                      <Field.Text name="tm_email" />
                    </DsField>
                  </Box>
                </>
              )}

              {step === 1 && (
                <>
                  <Box sx={{ fontSize: 14, fontWeight: 800, mb: '10px' }}>Serviço e valores</Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                      gap: '10px',
                      mb: '10px',
                    }}
                  >
                    <DsField label="Atividade (CNAE)" density="sm">
                      <Field.Autocomplete
                        name="atividade"
                        placeholder="Buscar atividade…"
                        options={atividades ?? []}
                        loading={loadingAtividades}
                        filterOptions={(x) => x}
                        onInputChange={(_e, value, reason) => {
                          if (reason === 'input') setAtividadeQuery(value);
                        }}
                        getOptionLabel={(opt) =>
                          typeof opt === 'string' ? opt : `${opt.codigo} · ${opt.descricao}`
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        noOptionsText="Nenhuma atividade"
                      />
                    </DsField>

                    <DsField label="Alíquota" density="sm">
                      <Field.Text
                        name="aliquota"
                        type="number"
                        slotProps={{
                          input: {
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          },
                        }}
                      />
                    </DsField>
                  </Box>

                  <Box sx={{ mb: '10px' }}>
                    <DsField label="Serviço (Lista LC 116)" density="sm">
                      <Field.Autocomplete
                        name="servico"
                        placeholder="Buscar serviço…"
                        options={servicos ?? []}
                        loading={loadingServicos}
                        filterOptions={(x) => x}
                        onInputChange={(_e, value, reason) => {
                          if (reason === 'input') setServicoQuery(value);
                        }}
                        getOptionLabel={(opt) =>
                          typeof opt === 'string' ? opt : `${opt.codigo} · ${opt.descricao}`
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        noOptionsText="Nenhum serviço"
                      />
                    </DsField>
                  </Box>

                  <Box sx={{ mb: '10px' }}>
                    <DsField label="Discriminação do serviço prestado *" density="sm">
                      <Field.Text
                        name="discriminacao"
                        multiline
                        rows={2}
                        placeholder="Fornecimento de 120 coffee-breaks · evento 12/04/2025…"
                      />
                    </DsField>
                  </Box>

                  {fields.map((f, i) => (
                    <Box
                      key={f.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: fields.length > 1 ? '2fr 1fr 1fr 30px' : '2fr 1fr 1fr',
                        },
                        alignItems: 'end',
                        gap: '10px',
                        mb: '10px',
                      }}
                    >
                      <DsField label="Descrição" density="sm">
                        <Field.Text name={`items.${i}.descricao`} />
                      </DsField>
                      <DsField label="Qtde" density="sm">
                        <Field.Text name={`items.${i}.quantidade`} type="number" />
                      </DsField>
                      <DsField label="Vl. unitário" density="sm">
                        <Field.Text
                          name={`items.${i}.valor_unitario`}
                          type="number"
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            },
                          }}
                        />
                      </DsField>
                      {fields.length > 1 && (
                        <Box sx={{ pb: '3px' }}>
                          <DsIconButton
                            icon="excluir"
                            aria-label={`Remover o item ${i + 1}`}
                            onClick={() => remove(i)}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}

                  {/* O artboard mostra um item só; a nota aceita vários e o
                      controle de acrescentar fica em `.btnp` neutro. */}
                  <DsButton tone="neutral" icon="mais" onClick={() => append({ ...EMPTY_ITEM })}>
                    Adicionar item
                  </DsButton>
                </>
              )}

              {step === 2 && (
                <>
                  <Box sx={{ fontSize: 14, fontWeight: 800, mb: '4px' }}>Revisar e emitir</Box>
                  <Box sx={{ fontSize: 12, color: 'text.secondary', mb: '12px' }}>
                    Confira os dados antes de emitir. A autenticação é gerada após a emissão.
                  </Box>

                  <DsOverline>Tomador</DsOverline>
                  <PilhaResumo>
                    <LinhaResumo rotulo="Nome / razão social" valor={razaosocial} />
                    <LinhaResumo rotulo="CPF/CNPJ" valor={cpfcnpj} />
                  </PilhaResumo>

                  <Box sx={{ mt: '10px' }}>
                    <DsOverline>Serviço</DsOverline>
                  </Box>
                  <PilhaResumo>
                    <LinhaResumo
                      rotulo="Atividade (CNAE)"
                      valor={atividade ? `${atividade.codigo} · ${atividade.descricao}` : ''}
                    />
                    <LinhaResumo
                      rotulo="Serviço (Lista LC 116)"
                      valor={servico ? `${servico.codigo} · ${servico.descricao}` : ''}
                    />
                    <LinhaResumo rotulo="Discriminação" valor={discriminacao} />
                    {items.map((item, i) => (
                      <LinhaResumo
                        key={`item-${i}`}
                        rotulo={i === 0 ? 'Item' : `Item ${i + 1}`}
                        valor={
                          item.descricao
                            ? `${item.descricao} · ${Number(item.quantidade) || 0} × ${formatCurrency(Number(item.valor_unitario) || 0)}`
                            : ''
                        }
                      />
                    ))}
                  </PilhaResumo>

                  <Box sx={{ mt: '10px' }}>
                    <DsOverline>Valores</DsOverline>
                  </Box>
                  <PilhaResumo>
                    <LinhaResumo rotulo="Valor dos serviços" valor={formatCurrency(valorTotal)} />
                    <LinhaResumo rotulo={rotuloIss} valor={formatCurrency(issValor)} />
                    <LinhaResumo rotulo="INSS / COFINS / CSLL" valor={formatCurrency(0)} />
                    <LinhaResumo rotulo="Valor da nota" valor={formatCurrency(valorTotal)} forte />
                  </PilhaResumo>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      mt: '12px',
                    }}
                  >
                    <DsStatusChip tone="success">{SELO_SERIE}</DsStatusChip>
                  </Box>
                </>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  mt: '14px',
                }}
              >
                <DsButton tone="neutral" size="tall" onClick={voltar}>
                  Voltar
                </DsButton>

                {step < 2 ? (
                  <DsButton tone="primary" size="tall" onClick={avancar}>
                    {step === 1 ? 'Revisar nota' : 'Continuar'}
                  </DsButton>
                ) : (
                  <DsButton
                    tone="primary"
                    size="tall"
                    type="submit"
                    disabled={!canEmit || enviando}
                  >
                    Emitir NFS-e
                  </DsButton>
                )}
              </Box>
            </Card>
          </Box>

          {resumo}
        </Box>
      </TxForm>
    </DashboardContent>
  );
}
